import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Calendar as CalendarIcon, TrendingUp, Info, GitCompare, ChevronDown, ChevronUp } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { addDays, format, subDays, differenceInDays, isAfter, isBefore, parseISO } from 'date-fns'
import { cn } from '@/lib/utils'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { fetcher } from '@/hooks'
import { useDatePicker2 } from '@/components/MyDatePicker'
import { troasQueryFromPicker } from '../../troas-api-query'
import { shortCampaignLabel } from '../../utils'

// 给定某 campaign 的 dailyData 与日期窗口，计算 ROAS 曲线（用于主 campaign 与对比 campaign 图表）
function getCurveForWindow(dailyData = [], start, end) {
  const dailyDataMap = new Map()
  dailyData.forEach((item) => {
    dailyDataMap.set(item.install_date, item)
  })
  const dates = []
  let curr = new Date(start)
  const endDate = new Date(end)
  while (curr <= endDate) {
    dates.push(format(curr, 'yyyy-MM-dd'))
    curr = addDays(curr, 1)
  }
  let totalCost = 0
  const totalRevenueByDay = new Array(91).fill(0)
  let maxCompleteDay = null
  for (const dStr of dates) {
    const entry = dailyDataMap.get(dStr)
    if (!entry) continue
    totalCost += entry.cost || 0
    let maxDayForEntry = -1
    let cohortAcc = 0
    for (let d = 0; d <= 90; d++) {
      const dayKey = `day${d}`
      if (entry.days?.[dayKey] !== undefined) {
        cohortAcc += entry.days[dayKey]
        maxDayForEntry = d
      }
      totalRevenueByDay[d] += cohortAcc
    }
    if (maxDayForEntry >= 0) {
      maxCompleteDay = maxCompleteDay === null ? maxDayForEntry : Math.min(maxCompleteDay, maxDayForEntry)
    }
  }
  if (totalCost === 0) return null
  const fullCurve = totalRevenueByDay.map((r) => r / totalCost)
  return { fullCurve, maxCompleteDay: maxCompleteDay ?? 0 }
}

const COMPARE_CHART_COLORS = ['#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4']

// 从 URL 解析日期范围 "start,end"，返回 { from, to } 或 null
function parseDateRangeFromUrl(str) {
  if (!str || typeof str !== 'string') return null
  const parts = str.split(',').map(s => s.trim()).filter(Boolean)
  if (parts.length !== 2) return null
  const [start, end] = parts
  const from = new Date(start)
  const to = new Date(end)
  if (isNaN(from.getTime()) || isNaN(to.getTime()) || from > to) return null
  return { from, to }
}

const PredictionPanel = ({
  campaignId,
  allDailyData = [],
  compareIds = [],
  onCompareDates,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const pickerStart = useDatePicker2((s) => s.startDate)
  const pickerEnd = useDatePicker2((s) => s.endDate)
  const troasApiQuery = useMemo(
    () => troasQueryFromPicker(pickerStart, pickerEnd),
    [pickerStart, pickerEnd],
  )

  const [isOpen, setIsOpen] = useState(() => searchParams.get('pred_open') === '1')
  const [date, setDate] = useState(() => {
    const parsed = parseDateRangeFromUrl(searchParams.get('pred_range'))
    if (parsed) return parsed
    const dur = parseInt(searchParams.get('pred_duration'), 10)
    const d = [7, 14, 30].includes(dur) ? dur : 14
    const buffer = 1
    const to = subDays(new Date(), d + buffer)
    const from = subDays(to, d - 1)
    return { from, to }
  })
  const [metric, setMetric] = useState(() => {
    const m = searchParams.get('pred_metric')
    return m === 'multiplier' ? 'multiplier' : 'roas'
  })
  const [duration, setDuration] = useState(() => {
    const dur = parseInt(searchParams.get('pred_duration'), 10)
    return [7, 14, 30].includes(dur) ? dur : 14
  })
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [result, setResult] = useState(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(() => searchParams.get('pred_collapsed') === '1')
  const durationEffectSkippedRef = useRef(!!searchParams.get('pred_range'))

  // 对比 campaign 仍单独请求；主 campaign 的 /troas 由 campaign-detail-page 统一请求后下发 Analysis
  const { data: compareResults } = useSWR(
    compareIds?.length > 0
      ? [
          'compare-all',
          [...compareIds].sort().join(','),
          JSON.stringify(troasApiQuery),
        ]
      : null,
    async ([, idsStr]) => {
      const ids = idsStr.split(',')
      const fetchOne = async (cid) => {
        const [roasRaw, camp, prediction, day90] = await Promise.all([
          fetcher([`/troas/${cid}`, troasApiQuery]),
          fetcher([`/campaigns/${cid}`]),
          fetcher([`/campaigns/${cid}/prediction`]),
          fetcher([`/campaigns/${cid}/roas/90`]),
        ])
        return { cid, roasRaw, camp, prediction, day90 }
      }
      return Promise.all(ids.map(fetchOne))
    },
    { revalidateOnFocus: false, keepPreviousData: true },
  )

  const compareCampaignsList = useMemo(() => {
    if (!compareResults || !Array.isArray(compareResults)) return []
    return compareResults.map((r) => ({
      cid: r.cid,
      dailyData: r.roasRaw?.dailyData ?? [],
      name: r.camp?.name || r.cid,
    }))
  }, [compareResults])

  // 当 duration 变化时更新 date（用户切换 D7/D14/D30 时）；若从 URL 加载了 pred_range 则跳过初次运行
  useEffect(() => {
    if (durationEffectSkippedRef.current) {
      durationEffectSkippedRef.current = false
      return
    }
    const buffer = 1
    const to = subDays(new Date(), duration + buffer)
    const from = subDays(to, duration - 1)
    setDate({ from, to })
  }, [duration])

  // 状态变化时同步到 URL（使用 window.location.search 以保留其他组件的参数）
  useEffect(() => {
    const next = new URLSearchParams(window.location.search)
    if (isOpen) {
      next.set('pred_open', '1')
      next.set('pred_collapsed', isCollapsed ? '1' : '0')
      if (date?.from && date?.to) {
        next.set('pred_range', `${format(date.from, 'yyyy-MM-dd')},${format(date.to, 'yyyy-MM-dd')}`)
      }
      next.set('pred_metric', metric)
      next.set('pred_duration', String(duration))
      if (selectedMatch?.startDate && selectedMatch?.endDate) {
        next.set('pred_selected', `${selectedMatch.startDate},${selectedMatch.endDate}`)
      }
      // 不主动删除 pred_selected，以保留 URL 初始值供「从 URL 定位」逻辑使用
    } else {
      next.delete('pred_open')
      next.delete('pred_collapsed')
      next.delete('pred_range')
      next.delete('pred_metric')
      next.delete('pred_duration')
      next.delete('pred_selected')
    }
    const nextStr = next.toString()
    const currentStr = window.location.search.slice(1)
    if (nextStr !== currentStr) {
      setSearchParams(next, { replace: true })
    }
  }, [isOpen, isCollapsed, date, metric, duration, selectedMatch])

  // pred_open=1 且数据已加载时自动执行分析
  const hasAutoAnalyzedRef = useRef(false)
  useEffect(() => {
    if (
      searchParams.get('pred_open') === '1' &&
      allDailyData?.length > 0 &&
      date?.from &&
      date?.to &&
      !hasAutoAnalyzedRef.current
    ) {
      hasAutoAnalyzedRef.current = true
      handleAnalyze()
    }
  }, [searchParams.get('pred_open'), allDailyData?.length, date?.from, date?.to])

  // 分析完成后，根据 URL 的 pred_selected 定位到对应相似时段
  useEffect(() => {
    const predSelected = searchParams.get('pred_selected')
    if (!predSelected || !result?.matches?.length) return
    const parts = predSelected.split(',').map(s => s.trim()).filter(Boolean)
    if (parts.length !== 2) return
    const [start, end] = parts
    const match = result.matches.find(m => m.startDate === start && m.endDate === end)
    if (match) {
      setSelectedMatch(match)
    }
  }, [result, searchParams])

  const handleAnalyze = () => {
    if (!date?.from || !date?.to || !allDailyData.length) return
    
    setIsAnalyzing(true)
    setIsOpen(true)
    setIsCollapsed(false)
    setSelectedMatch(null)

    // Simulate async for better UX (UI doesn't freeze)
    setTimeout(() => {
      const targetStart = date.from
      const targetEnd = date.to
      const durationDays = differenceInDays(targetEnd, targetStart) + 1

      if (durationDays < 1) {
        setIsAnalyzing(false)
        return
      }

      // 1. Build Map for quick access
      const dailyDataMap = new Map()
      allDailyData.forEach(item => {
        dailyDataMap.set(item.install_date, item)
      })

      // 2. Helper: Calculate metrics for a window
      const calculateMetrics = (start, end) => {
        const dates = []
        let curr = new Date(start)
        while (curr <= end) {
          dates.push(format(curr, 'yyyy-MM-dd'))
          curr = addDays(curr, 1)
        }

        let totalCost = 0
        const totalRevenueByDay = new Array(91).fill(0)
        let maxDayReached = 0
        // 有完整数据的最大 D：窗口内每个 install_date 都有数据的最大的 day 索引（取 min）
        let maxCompleteDay = null

        for (const dStr of dates) {
          const entry = dailyDataMap.get(dStr)
          if (!entry) continue
          totalCost += entry.cost || 0
          let maxDayForEntry = -1
          let cohortAcc = 0
          for (let d = 0; d <= 90; d++) {
            const dayKey = `day${d}`
            if (entry.days?.[dayKey] !== undefined) {
              cohortAcc += entry.days[dayKey]
              if (d > maxDayReached) maxDayReached = d
              maxDayForEntry = d
            }
            totalRevenueByDay[d] += cohortAcc
          }
          if (maxDayForEntry >= 0) {
            maxCompleteDay = maxCompleteDay === null ? maxDayForEntry : Math.min(maxCompleteDay, maxDayForEntry)
          }
        }

        if (totalCost === 0) return null

        const roasVector = []
        for (let d = 0; d <= duration; d++) {
          roasVector.push(totalRevenueByDay[d] / totalCost)
        }

        return {
          roasVector,
          totalCost,
          d90Roas: totalRevenueByDay[90] / totalCost,
          fullCurve: totalRevenueByDay.map(r => r / totalCost),
          maxDayReached,
          maxCompleteDay: maxCompleteDay ?? 0
        }
      }

      // 3. Target metrics：同时计算 ROAS 与倍率向量，用于双维度相似度
      const targetMetrics = calculateMetrics(targetStart, targetEnd)
      if (!targetMetrics) {
        setIsAnalyzing(false)
        setResult(null)
        return
      }

      const targetRoasVector = targetMetrics.roasVector
      const targetBase = targetRoasVector[0] || 1
      const targetMultiplierVector = targetRoasVector.map(v => v / targetBase)

      // 4. Sliding window search：每个候选计算 ROAS 距离与倍率距离
      const matches = []
      const availableDates = allDailyData
        .map(d => d.install_date)
        .sort()
        .map(d => parseISO(d))

      if (availableDates.length === 0) {
        setIsAnalyzing(false)
        return
      }

      const firstDate = availableDates[0]
      const lastPossibleEnd = subDays(new Date(), 91) // Must have D90
      const lastPossibleStart = subDays(lastPossibleEnd, durationDays - 1)

      const euclidean = (a, b) => {
        let sumSq = 0
        for (let i = 0; i < a.length; i++) {
          const diff = (a[i] || 0) - (b[i] || 0)
          sumSq += diff * diff
        }
        return Math.sqrt(sumSq)
      }

      let curr = new Date(firstDate)
      while (curr <= lastPossibleStart) {
        const wStart = curr
        const wEnd = addDays(wStart, durationDays - 1)
        const wStartStr = format(wStart, 'yyyy-MM-dd')
        const wEndStr = format(wEnd, 'yyyy-MM-dd')

        const isOverlap = isBefore(wStart, addDays(targetEnd, 1)) && isAfter(wEnd, subDays(targetStart, 1))
        if (!isOverlap) {
          const metrics = calculateMetrics(wStart, wEnd)
          if (metrics && metrics.totalCost > 0 && metrics.maxCompleteDay >= 90) {
            const roasVector = metrics.roasVector
            const base = roasVector[0] || 1
            const multiplierVector = roasVector.map(v => v / base)

            const distanceRoas = euclidean(roasVector, targetRoasVector)
            const distanceMultiplier = euclidean(multiplierVector, targetMultiplierVector)

            matches.push({
              startDate: wStartStr,
              endDate: wEndStr,
              scoreRoas: distanceRoas,
              scoreMultiplier: distanceMultiplier,
              d90Roas: metrics.d90Roas,
              roasVector: metrics.roasVector,
              fullCurve: metrics.fullCurve,
              maxDayReached: metrics.maxDayReached,
              maxCompleteDay: metrics.maxCompleteDay
            })
          }
        }
        curr = addDays(curr, 1)
      }

      // 5. 双维度结合排序：对 ROAS 与倍率距离分别 min-max 归一化后取平均，按综合距离升序
      if (matches.length > 0) {
        const minRoas = Math.min(...matches.map(m => m.scoreRoas))
        const maxRoas = Math.max(...matches.map(m => m.scoreRoas))
        const rangeRoas = maxRoas - minRoas || 1
        const minMult = Math.min(...matches.map(m => m.scoreMultiplier))
        const maxMult = Math.max(...matches.map(m => m.scoreMultiplier))
        const rangeMult = maxMult - minMult || 1
        matches.forEach(m => {
          const normRoas = (m.scoreRoas - minRoas) / rangeRoas
          const normMult = (m.scoreMultiplier - minMult) / rangeMult
          m.score = (normRoas + normMult) / 2
        })
      }

      matches.sort((a, b) => a.score - b.score)

      // 6. NMS (Distinct matches)
      const distinctMatches = []
      for (const m of matches) {
        const isTooClose = distinctMatches.some(dm => {
          const startA = parseISO(m.startDate).getTime()
          const endA = parseISO(m.endDate).getTime()
          const startB = parseISO(dm.startDate).getTime()
          const endB = parseISO(dm.endDate).getTime()
          
          const overlap = Math.max(0, Math.min(endA, endB) - Math.max(startA, startB))
          const duration = endA - startA
          return (overlap / duration) > 0.5
        })

        if (!isTooClose) {
          distinctMatches.push(m)
        }
        if (distinctMatches.length >= 20) break
      }

      setResult({
        target: {
          startDate: format(targetStart, 'yyyy-MM-dd'),
          endDate: format(targetEnd, 'yyyy-MM-dd'),
          fullCurve: targetMetrics.fullCurve,
          roasVector: targetMetrics.roasVector,
          maxDayReached: targetMetrics.maxDayReached,
          maxCompleteDay: targetMetrics.maxCompleteDay
        },
        matches: distinctMatches
      })
      setIsAnalyzing(false)
    }, 10)
  }

  const target = result?.target
  const matches = result?.matches || []
  const currentMatch = target && matches.length > 0 ? (selectedMatch || matches[0]) : null

  // 构建图表数据：主 campaign 的 Target/Matched；若有对比 campaign 则追加其目标时段与匹配时段曲线
  const chartData = useMemo(() => {
    const out = []
    if (!target || !currentMatch) return out
    const fullTargetCurve = target.fullCurve || []
    const matchCurve = currentMatch.fullCurve || []
    const targetMaxComplete = target.maxCompleteDay ?? target.maxDayReached ?? 0
    const targetStart = target.startDate
    const targetEnd = target.endDate
    const matchStart = currentMatch.startDate
    const matchEnd = currentMatch.endDate

    // 对比 campaign 在目标时段、匹配时段的曲线
    const compareCurves = []
    if (Array.isArray(compareCampaignsList) && compareCampaignsList.length > 0) {
      compareCampaignsList.forEach((cc) => {
        const targetCurve = getCurveForWindow(cc.dailyData, targetStart, targetEnd)
        const matchedCurve = getCurveForWindow(cc.dailyData, matchStart, matchEnd)
        compareCurves.push({
          targetCurve: targetCurve?.fullCurve ?? null,
          targetMaxComplete: targetCurve?.maxCompleteDay ?? 0,
          matchedCurve: matchedCurve?.fullCurve ?? null,
          name: cc.name,
        })
      })
    }

    for (let i = 0; i <= 90; i++) {
      const dayData = { day: i }
      const isTargetAvailable = i <= targetMaxComplete
      const isMatchedAvailable = i <= 90
      if (metric === 'multiplier') {
        dayData.Target = (isTargetAvailable && fullTargetCurve[0]) ? fullTargetCurve[i] / fullTargetCurve[0] : null
        dayData.Matched = (isMatchedAvailable && matchCurve[0]) ? matchCurve[i] / matchCurve[0] : null
      } else {
        dayData.Target = isTargetAvailable ? fullTargetCurve[i] : null
        dayData.Matched = isMatchedAvailable ? matchCurve[i] : null
      }
      compareCurves.forEach((cc, idx) => {
        const tKey = `Target_c${idx}`
        const mKey = `Matched_c${idx}`
        const tAvail = cc.targetCurve && i <= cc.targetMaxComplete
        const mAvail = cc.matchedCurve && i <= 90
        if (metric === 'multiplier') {
          dayData[tKey] = (tAvail && cc.targetCurve[0]) ? cc.targetCurve[i] / cc.targetCurve[0] : null
          dayData[mKey] = (mAvail && cc.matchedCurve[0]) ? cc.matchedCurve[i] / cc.matchedCurve[0] : null
        } else {
          dayData[tKey] = tAvail ? cc.targetCurve[i] : null
          dayData[mKey] = mAvail ? cc.matchedCurve[i] : null
        }
      })
      out.push(dayData)
    }
    return out
  }, [target, currentMatch, metric, compareCampaignsList])

  const compareChartMeta = useMemo(() => {
    if (!Array.isArray(compareCampaignsList) || compareCampaignsList.length === 0) return []
    return compareCampaignsList.map((cc, idx) => ({
      idx,
      shortName: shortCampaignLabel(cc.name),
      color: COMPARE_CHART_COLORS[idx % COMPARE_CHART_COLORS.length],
    }))
  }, [compareCampaignsList])

  return (
    <Card className="mb-4">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            相似度匹配 ROAS (D90)
          </CardTitle>
          <div className="flex items-center space-x-2 ml-4">
            <Checkbox
              id="metric-mode"
              checked={metric === 'multiplier'}
              onCheckedChange={(checked) => setMetric(checked ? 'multiplier' : 'roas')}
            />
            <Label htmlFor="metric-mode" className="text-xs font-normal cursor-pointer">
              倍率对比
            </Label>
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
              <SelectTrigger className="h-8 w-[80px] text-xs">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">D7</SelectItem>
                <SelectItem value="14">D14</SelectItem>
                <SelectItem value="30">D30</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  'justify-start text-left font-normal h-8',
                  !date && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, 'LLL dd')} -{' '}
                      {format(date.to, 'LLL dd, y')}
                    </>
                  ) : (
                    format(date.from, 'LLL dd, y')
                  )
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={setDate}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>

          <Button size="sm" onClick={handleAnalyze} disabled={isAnalyzing || !allDailyData.length}>
            {isAnalyzing ? 'Analyzing...' : 'Analyze'}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      
      {isOpen && !isCollapsed && (
        <CardContent className="pt-0 pb-4 px-4">
          {result && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
              <div className="lg:col-span-2 h-[300px] border rounded-md p-2 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} domain={[0, 90]} />
                    <YAxis tick={{ fontSize: 12 }} domain={['auto', 'auto']} />
                    <Tooltip 
                      formatter={(value) => value?.toFixed(4)}
                      labelFormatter={(day) => `Day ${day}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Target"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      name={target?.startDate && target?.endDate ? `${format(new Date(target.startDate), 'MM/dd')}~${format(new Date(target.endDate), 'MM/dd')}` : ''}
                    />
                    <Line
                      type="monotone"
                      dataKey="Matched"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name={currentMatch?.startDate && currentMatch?.endDate ? `${format(new Date(currentMatch.startDate), 'MM/dd')}~${format(new Date(currentMatch.endDate), 'MM/dd')}` : ''}
                    />
                    {compareChartMeta.map(({ idx, shortName, color }) => (
                      <React.Fragment key={`compare-${idx}`}>
                        <Line
                          type="monotone"
                          dataKey={`Target_c${idx}`}
                          stroke={color}
                          strokeWidth={1.5}
                          dot={false}
                          activeDot={{ r: 3 }}
                          connectNulls
                          name={target?.startDate && target?.endDate ? `${shortName} ${format(new Date(target.startDate), 'MM/dd')}~${format(new Date(target.endDate), 'MM/dd')}` : shortName}
                        />
                        <Line
                          type="monotone"
                          dataKey={`Matched_c${idx}`}
                          stroke={color}
                          strokeWidth={1.5}
                          strokeDasharray="5 5"
                          dot={false}
                          activeDot={{ r: 3 }}
                          connectNulls
                          name={currentMatch?.startDate && currentMatch?.endDate ? `${shortName} ${format(new Date(currentMatch.startDate), 'MM/dd')}~${format(new Date(currentMatch.endDate), 'MM/dd')}` : `${shortName} (对比)`}
                        />
                      </React.Fragment>
                    ))}
                    <ReferenceLine x={duration} stroke="#ccc" label={`D${duration}`} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="lg:col-span-1 border rounded-md p-2 overflow-y-auto max-h-[300px]">
                <h4 className="font-semibold text-sm mb-2 px-1">相似时段</h4>
                <div className="space-y-1">
                  {matches.map((match, idx) => {
                    const isSelected = (selectedMatch === match) || (!selectedMatch && idx === 0)
                    return (
                      <div
                        key={`${match.startDate}-${match.endDate}`}
                        className={cn(
                          "text-xs p-2 rounded cursor-pointer flex justify-between items-center transition-colors border",
                          isSelected ? "bg-primary/10 border-primary/30 text-primary font-medium" : "hover:bg-muted border-transparent"
                        )}
                        onClick={() => setSelectedMatch(match)}
                      >
                        <div>
                          <div className="font-medium">
                            {format(new Date(match.startDate), 'MM/dd')} ~ {format(new Date(match.endDate), 'MM/dd')}
                          </div>
                          <div className="text-muted-foreground text-[10px]">
                            {format(new Date(match.startDate), 'yyyy')} · Sim: <span className="text-primary font-medium">{(100 / (1 + match.score)).toFixed(1)}%</span>
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1">
                          <div className="font-bold text-primary">
                            D90: {match.d90Roas.toFixed(3)}
                          </div>
                          {onCompareDates && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[10px] text-primary hover:text-primary border-primary/50"
                              onClick={(e) => {
                                e.stopPropagation()
                                // 对比区间 = 该相似时段；主区间 = Analyze 选定的目标时段
                                const mainDates = date?.from && date?.to
                                  ? [format(date.from, 'yyyy-MM-dd'), format(date.to, 'yyyy-MM-dd')]
                                  : null
                                onCompareDates([match.startDate, match.endDate], mainDates)
                              }}
                            >
                              <GitCompare className="w-3 h-3 mr-0.5" />
                              Compare
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {result && currentMatch && (
            <div className="mt-4 p-3 bg-muted/50 rounded-md flex items-center gap-4 text-sm">
              <Info className="w-4 h-4 text-blue-500" />
              <div>
                <div>
                  <strong>预测结论：</strong>基于选定的相似时段 ({format(new Date(currentMatch.startDate), 'yyyy-MM-dd')} ~ {format(new Date(currentMatch.endDate), 'yyyy-MM-dd')}，相似度 <span className="text-primary font-bold">{(100 / (1 + currentMatch.score)).toFixed(1)}%</span>)，预期 <strong>D90 ROAS</strong> 约为 <span className="text-lg font-bold text-primary">{currentMatch.d90Roas.toFixed(3)}</span>。
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  <strong>算法说明：</strong>相似度同时按 ROAS 与倍率两维计算欧式距离，归一化后取平均得到综合相似度并排序。
                </div>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}

export default PredictionPanel
